import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Zap, Target } from 'lucide-react';

export function NotificationToast({ notification, onClose, duration = 3000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  // Il notification può avere data nested o essere flat
  const data = notification.data || notification;
  
  // Determina colore e gradiente in base allo score
  const getScoreStyle = (score) => {
    if (score >= 90) {
      return {
        gradient: 'from-emerald-500 via-green-500 to-emerald-600',
        border: 'border-emerald-400/50',
        glow: 'shadow-emerald-500/50',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-300'
      };
    }
    if (score >= 75) {
      return {
        gradient: 'from-amber-500 via-orange-500 to-amber-600',
        border: 'border-amber-400/50',
        glow: 'shadow-amber-500/50',
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-300'
      };
    }
    return {
      gradient: 'from-slate-600 via-gray-600 to-slate-700',
      border: 'border-slate-400/50',
      glow: 'shadow-slate-500/50',
      iconBg: 'bg-slate-500/20',
      iconColor: 'text-slate-300'
    };
  };

  const style = getScoreStyle(data?.current_score || 0);

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed top-4 right-4 z-[9999]
        max-w-md w-full sm:w-96
        bg-gradient-to-br ${style.gradient}
        border ${style.border}
        backdrop-blur-xl
        text-white
        rounded-xl shadow-2xl ${style.glow}
        transform transition-all duration-500 ease-out
        ${isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'}
        overflow-hidden
      `}
      style={{
        boxShadow: `0 20px 60px -15px rgba(0, 0, 0, 0.5), 0 0 30px -5px rgba(59, 130, 246, 0.3)`
      }}
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,.1)_50%,transparent_75%,transparent_100%)] bg-[length:20px_20px] animate-[slide_20s_linear_infinite]"></div>
      </div>

      <div className="relative p-5 flex items-start gap-4">
        {/* Icona con glow effect */}
        <div className={`flex-shrink-0 ${style.iconBg} p-3 rounded-xl border ${style.border} backdrop-blur-sm`}>
          <TrendingUp className={`w-6 h-6 ${style.iconColor} drop-shadow-lg`} />
        </div>

        {/* Contenuto */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-lg tracking-tight">
                {data?.symbol || 'N/A'}
              </h4>
              <span className={`px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider ${style.iconBg} border ${style.border}`}>
                {data?.direction || 'LONG'}
              </span>
            </div>
            <button
              onClick={() => {
                setIsVisible(false);
                setTimeout(onClose, 300);
              }}
              className="text-white/70 hover:text-white transition-all hover:rotate-90 p-1 rounded-lg hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-sm text-white/95 mb-3 font-medium leading-relaxed">
            {data?.message || 'Score aumentato!'}
          </p>

          {/* Score con design tecnologico */}
          {data?.current_score && (
            <div className="mb-3 p-3 bg-black/20 rounded-lg border border-white/10 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-300" />
                  <span className="text-xs text-white/70 font-medium">SCORE</span>
                </div>
                <div className="flex items-center gap-2">
                  {data?.previous_score && (
                    <>
                      <span className="text-sm font-mono font-bold text-white/60">{data.previous_score}%</span>
                      <span className="text-emerald-300 font-bold">→</span>
                    </>
                  )}
                  <span className="text-lg font-mono font-bold text-emerald-300">{data.current_score}%</span>
                  {data?.score_increase && (
                    <span className="px-2 py-0.5 bg-emerald-500/30 text-emerald-200 text-xs font-bold rounded border border-emerald-400/30">
                      +{data.score_increase}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AOI Info con icona */}
          {data?.aoi_distance_pips !== null && data?.aoi_distance_pips !== undefined && (
            <div className="mb-3 flex items-center gap-2 text-xs text-white/80">
              <Target className="w-4 h-4 text-cyan-300" />
              <span className="font-medium">AOI:</span>
              <span className="font-mono font-bold text-cyan-300">{data.aoi_distance_pips} pips</span>
              {data?.aoi_level && (
                <span className="ml-2 font-mono text-white/60">@ {data.aoi_level.toFixed(5)}</span>
              )}
            </div>
          )}

          {/* Badge Strategy */}
          {data?.preset && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/30 backdrop-blur-sm rounded-lg text-xs font-semibold border border-white/10 text-white/90">
                <Zap className="w-3 h-3" />
                {data.preset.replace(/_/g, ' ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar animato */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
        <div 
          className="h-full bg-white/30 animate-shrink"
          style={{
            animation: `shrink ${duration}ms linear forwards`
          }}
        />
      </div>
    </div>
  );
}

